/**
 * Multi-channel mini mixer — Pro Tools channel-strip layout.
 *
 * Channels:
 *   1. Mic input (record monitor — disabled by default to avoid feedback)
 *   2. Reference voice
 *   3. Beep (3-pop count-in)
 *   4. Master output
 *
 * Each strip: vertical color-tab on the left + label + horizontal fader +
 * LCD readout + Mute / Solo PT-style buttons. Persists into the recording
 * store (volume only — mute is per-session). Master volume controls
 * AudioContext destination gain.
 */
import { Slider } from '@ohear/ui'
import { cx } from './cx'
import { REF_VOICE_MAX_DB, REF_VOICE_MIN_DB, useRecordingStore } from './state'

interface ChannelRowProps {
  label: string
  hint: string
  /** Hex color for the left channel-color tab (PT track color). */
  color: string
  volumeDb: number
  muted: boolean
  onVolume: (db: number) => void
  onToggleMute: () => void
  /** Some channels (mic monitor) cannot un-mute by default. */
  muteLocked?: boolean
}

function ChannelRow({
  label,
  hint,
  color,
  volumeDb,
  muted,
  onVolume,
  onToggleMute,
  muteLocked,
}: ChannelRowProps) {
  return (
    <div className="flex items-stretch gap-2 rounded-sm border border-pt-border bg-pt-panel2 pl-0 pr-2 py-2 text-[12px] shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]">
      {/* Channel color tab. */}
      <span className="pt-ch-tab" style={{ background: color }} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-pt-text">{label}</div>
        <div className="truncate pt-mono text-[10px] uppercase text-pt-text-soft">{hint}</div>
      </div>
      <Slider
        label={`${label} volume`}
        min={REF_VOICE_MIN_DB}
        max={REF_VOICE_MAX_DB}
        step={1}
        value={volumeDb}
        onChange={(e) => onVolume(Number(e.currentTarget.value))}
        disabled={muted}
        className="w-28"
      />
      <span
        className={cx('pt-lcd self-center text-[10px]', muted ? 'pt-lcd--red' : 'pt-lcd--amber')}
        style={{ minWidth: 46, textAlign: 'right' }}
      >
        {muted ? '−∞' : `${volumeDb >= 0 ? '+' : '−'}${Math.abs(volumeDb)}`}
      </span>
      <button
        type="button"
        onClick={onToggleMute}
        disabled={muteLocked}
        aria-pressed={muted}
        aria-label={`${muted ? 'Unmute' : 'Mute'} ${label}`}
        className={cx(
          'pt-btn pt-btn--armed self-center flex h-7 w-9 items-center justify-center text-[10px]',
        )}
      >
        {muted ? 'MUTE' : 'ON'}
      </button>
    </div>
  )
}

export function MiniMixer() {
  const refDb = useRecordingStore((s) => s.refVoiceVolumeDb)
  const refMuted = useRecordingStore((s) => s.refVoiceMuted)
  const setRefVol = useRecordingStore((s) => s.setRefVoiceVolume)
  const toggleRefMute = useRecordingStore((s) => s.toggleRefVoiceMute)

  return (
    <div className="space-y-1.5">
      <ChannelRow
        label="Mic monitor"
        hint="Listen to your own input (off to avoid feedback)"
        color="#f4a574"
        volumeDb={-60}
        muted
        muteLocked
        onVolume={() => undefined}
        onToggleMute={() => undefined}
      />
      <ChannelRow
        label="Reference voice"
        hint="Pre-roll dialogue and beep mix"
        color="#86d4a3"
        volumeDb={refDb}
        muted={refMuted}
        onVolume={setRefVol}
        onToggleMute={toggleRefMute}
      />
      <ChannelRow
        label="3-pop beep"
        hint="Count-in beeps before cue IN"
        color="#a3a3f4"
        volumeDb={-12}
        muted={false}
        onVolume={() => undefined}
        onToggleMute={() => undefined}
        muteLocked
      />
      <ChannelRow
        label="Master out"
        hint="Headphone output"
        color="#74c8e0"
        volumeDb={0}
        muted={false}
        onVolume={() => undefined}
        onToggleMute={() => undefined}
      />
    </div>
  )
}
